import Box from '@totallymoney/ui/components/Box'
import Grid from '@totallymoney/ui/components/Grid'
import Heading from '@totallymoney/ui/components/Heading'
import LinkButton from '@totallymoney/ui/components/LinkButton'
import LinkWithChevron from '@totallymoney/ui/components/LinkWithChevron'
import Stack from '@totallymoney/ui/components/Stack'
import theme from '@totallymoney/ui/theme'

import {
    ApplicationURLTriggerLocation,
    GuaranteedOfferType,
    OfferType,
    ScoredCreditCardProduct,
} from '@appTypes/EligibilityApi.interface'
import { GetGuaranteedCreditLimitResponse } from '@appTypes/PagesApi.interface'

import BannerOffer from '@components/BannerOffer'
import CardMinimumCreditLimitHighlight from '@components/CardMinimumCreditLimitHighlight'
import CardPageContentBase from '@components/CardPageContentBase'
import CardPageImage from '@components/CardPageImage'
import CardPageItemProductDetail from '@components/CardPageItemProductDetail'
import CardPageProductLists from '@components/CardPageProductLists'
import CreditCardContent from '@components/CreditCardContent'
import { ProductDetailType } from '@components/CreditCardContent/components/Content.interface'
import CreditLimitModal from '@components/CreditLimitModal'
import EligibilityIndicator from '@components/EligibilityIndicator'
import GuaranteedOfferModal from '@components/GuaranteedOfferModal'
import { getHasOfferTypeContent } from '@components/GuaranteedOfferModal/GuaranteedOfferModal'
import GuaranteedOfferQuestion from '@components/GuaranteedOfferQuestion'
import MinimumCreditLimitModal from '@components/MinimumCreditLimitModal/MinimumCreditLimitModal'
import PreApprovedIndicator from '@components/PreApprovedIndicator'
import PreApprovedModal from '@components/PreApprovedModal/PreApprovedModal'
import RatesAndFees from '@components/RatesAndFees'
import RepresentativeExample from '@components/RepresentativeExample'
import RepresentativeExampleModal from '@components/RepresentativeExampleModal'
import TmExclusiveIcon from '@components/TmExclusiveIcon'

import useAppQueryState from '@hooks/useAppQueryState'
import useLogEvents from '@hooks/useLogEvents'
import useSetState from '@hooks/useSetState'

import useGetCreditCardApplicationUrl from '@routes/ScoredTableRoute/hooks/useGetCardApplicationUrl'
import useGetDifferenceInNewCreditCardsOffers from '@routes/ScoredTableRoute/hooks/useGetDifferenceInNewCreditCardsOffers'

import { getAppConfig } from '@utils/getAppConfig'
import { getCardApr } from '@utils/getCardApr'
import getGuaranteedOfferType from '@utils/getGuaranteedOfferType'
import { getIsProductPortraitImage } from '@utils/getIsProductPortraitImage'
import { getShowOfferDurationContent } from '@utils/getShowOfferDurationContent'

import { StyledGrid } from './ScoredCard.styled'
import ScoredCardDetailsTotallySureTooltipBanner from './components/ScoredCardDetailsTotallySureTooltipBanner'

interface ScoredCardProps {
    minCreditLimitData?: GetGuaranteedCreditLimitResponse | null
    scoredCard: ScoredCreditCardProduct
    thirdPartyReference: string
}

enum ModalType {
    AppAdoption = 'AppAdoption',
    BestMatch = 'BestMatch',
    CreditLimitModal = 'CreditLimitModal',
    GuaranteedOfferModal = 'GuaranteedOfferModal',
    MinimumCreditLimit = 'MinimumCreditLimit',
    PreApproved = 'PreApproved',
    RepresentativeExample = 'RepresentativeExample',
}

interface ModalState {
    activeCard: ScoredCreditCardProduct | null
    activeModal: ModalType | null
    creditCardApplicationUrl: string | null
}

export const defaultModalState: ModalState = {
    activeCard: null,
    activeModal: null,
    creditCardApplicationUrl: null,
}

const ScoredCard = ({
    scoredCard,
    thirdPartyReference,
    minCreditLimitData,
}: ScoredCardProps) => {
    const appConfig = getAppConfig()

    const { appQueryState, goToRouteWithAppState } = useAppQueryState()

    const logEvents = useLogEvents()
    const getCardApplicationUrl = useGetCreditCardApplicationUrl()

    const [modalState, updateModalState] =
        useSetState<ModalState>(defaultModalState)

    const guaranteedOfferType = getGuaranteedOfferType({
        creditCard: scoredCard,
    })
    const isNewOffer = useGetDifferenceInNewCreditCardsOffers().includes(
        scoredCard.productCode
    )

    const applicationUrl = getCardApplicationUrl({
        category: appQueryState.categoryId!,
        creditCard: scoredCard,
        creditCardIndex: 0,
        isBestMatchCard: false,
        thirdPartyReference,
        trigger: ApplicationURLTriggerLocation.Row,
        isNewOffer,
    })

    const onApplyPress = () => {
        logEvents.logCardApplyEvent({
            category: appQueryState.categoryId!,
            clickSource: appConfig.CLICK_SOURCE_WHITELABEL,
            isBestMatchCard: false,
            productCode: scoredCard.productCode,
            rowNumber: 0,
            score: scoredCard.score,
            status: scoredCard.status,
            trigger: ApplicationURLTriggerLocation.Row,
            isCounterOffer: scoredCard.guaranteedRate?.isCounterOffer ?? false,
            isNewOffer,
        })

        window.open(applicationUrl, '_blank')
    }

    const resetModalState = () => {
        updateModalState(defaultModalState)
    }

    const isExclusiveOffer = scoredCard.offerType === OfferType.TMExclusive
    const isMarketWideOffer = scoredCard.offerType === OfferType.MarketWide
    const isTotallySureOffer =
        guaranteedOfferType === GuaranteedOfferType.MarketingOffer

    return (
        <>
            <Grid>
                <Box mt="space30" mb={['space10', 'space20']} gridColumn="1/13">
                    <LinkWithChevron
                        href="#"
                        variant="100"
                        direction="left"
                        onClick={() => {
                            event!.preventDefault()
                            goToRouteWithAppState(appQueryState.category!)
                        }}
                    >
                        Back to cards
                    </LinkWithChevron>
                </Box>
            </Grid>
            <Grid>
                <Box gridColumn="1/13" mb={['space20', 'space40']}>
                    <Stack space="space20">
                        <Heading as="h1" variant="60" color="light">
                            {scoredCard.name}
                        </Heading>

                        {isExclusiveOffer ? (
                            <BannerOffer
                                prefix={<TmExclusiveIcon />}
                                title={scoredCard.offerHeading}
                                description={scoredCard.offerCopy}
                                backgroundColor="indigo30"
                            />
                        ) : null}

                        {isMarketWideOffer ? (
                            <BannerOffer
                                title={scoredCard.offerHeading}
                                description={scoredCard.offerCopy}
                                backgroundColor="lightBlue10"
                            />
                        ) : null}

                        {guaranteedOfferType ===
                            GuaranteedOfferType.GuaranteedCreditLimit ||
                        guaranteedOfferType ===
                            GuaranteedOfferType.GuaranteedAprAndCreditLimit ||
                        guaranteedOfferType ===
                            GuaranteedOfferType.PreApprovedAndGuaranteedCreditLimit ? (
                            <CardMinimumCreditLimitHighlight
                                variant="gcl"
                                title="Credit limit guaranteed"
                                description=""
                            />
                        ) : null}
                    </Stack>
                </Box>
            </Grid>

            {isTotallySureOffer ? (
                <StyledGrid>
                    <Box gridColumn="1/13" mb={['space20', 'space50']}>
                        <ScoredCardDetailsTotallySureTooltipBanner
                            onClick={() =>
                                updateModalState({
                                    activeModal: ModalType.GuaranteedOfferModal,
                                })
                            }
                        />
                    </Box>
                </StyledGrid>
            ) : null}

            <Grid>
                <Box
                    gridColumn="1/13"
                    display="grid"
                    gridGap={['space30', 'space50']}
                    mb="space50"
                >
                    <Box
                        gridColumn={['1/13', '1/13', '1/9']}
                        display="flex"
                        flexDirection={['column', 'row']}
                    >
                        <Box
                            maxWidth={['216px', '216px', '375px']}
                            mr={[0, 'space50']}
                            mb={['space30', 0]}
                            display="flex"
                            alignSelf={['center', 'flex-start']}
                        >
                            <CardPageImage
                                name={scoredCard.name}
                                cmsImageUrl={scoredCard.imageUrlLarge}
                                isPortraitImage={getIsProductPortraitImage(
                                    scoredCard.productCode
                                )}
                            />
                        </Box>

                        <Box
                            display="flex"
                            flexDirection="column"
                            justifyContent="space-between"
                        >
                            <Box pb="space30">
                                <CardPageContentBase>
                                    <CreditCardContent
                                        category={appQueryState.categoryId!}
                                        creditCard={scoredCard}
                                        minCreditLimitData={minCreditLimitData}
                                        onShowCreditLimitModal={() =>
                                            updateModalState({
                                                activeModal:
                                                    ModalType.CreditLimitModal,
                                            })
                                        }
                                        onShowMinimumCreditLimitModal={() =>
                                            updateModalState({
                                                activeModal:
                                                    ModalType.MinimumCreditLimit,
                                            })
                                        }
                                        isTotallySureOffer={isTotallySureOffer}
                                        renderProductDetail={detail => {
                                            if (
                                                detail.detailType ===
                                                ProductDetailType.setLimit
                                            ) {
                                                return <></>
                                            }
                                            return (
                                                <CardPageItemProductDetail
                                                    {...detail}
                                                />
                                            )
                                        }}
                                    />
                                </CardPageContentBase>
                            </Box>

                            <RepresentativeExample
                                item={scoredCard}
                                onTooltipPress={() =>
                                    updateModalState({
                                        activeModal:
                                            ModalType.RepresentativeExample,
                                    })
                                }
                            />
                        </Box>
                    </Box>

                    <Box
                        gridColumn={['1/13', '1/13', '9/13']}
                        display="flex"
                        alignItems="flex-start"
                        justifyContent={['center', 'center', 'flex-start']}
                        flexDirection="column"
                    >
                        {!scoredCard.isPreApproved &&
                        scoredCard.score > 0 &&
                        scoredCard.score < 10 ? (
                            <Box alignSelf="center" pb={[0, 'space30']}>
                                <EligibilityIndicator
                                    score={scoredCard.score * 10}
                                />
                            </Box>
                        ) : null}

                        {scoredCard.isPreApproved ? (
                            <Box
                                alignSelf="center"
                                pb={[0, 'space30']}
                                minWidth="200px"
                            >
                                <PreApprovedIndicator
                                    id={scoredCard.productCode}
                                    apr={getCardApr(scoredCard)}
                                    onPress={() =>
                                        updateModalState({
                                            activeModal: ModalType.PreApproved,
                                        })
                                    }
                                />
                            </Box>
                        ) : null}

                        <Box
                            position={['fixed', 'relative']}
                            alignSelf="center"
                            bottom={0}
                            left={0}
                            width={['100%', '350px', '260px']}
                            backgroundColor="white"
                            borderTop={[`solid 1px ${theme.indigo100}`, 0]}
                        >
                            <Box p={['space30', 0]}>
                                <LinkButton
                                    text="Apply"
                                    href={applicationUrl}
                                    isExternalLink
                                    variant="primary"
                                    data-testid="applyButtonDetails"
                                    rel="nofollow noopener"
                                    target="_blank"
                                    data-gtm-event="applyNow-row-postClick"
                                    onClick={evt => {
                                        evt.preventDefault()
                                        onApplyPress()
                                    }}
                                />
                            </Box>
                        </Box>
                    </Box>

                    {!isTotallySureOffer &&
                    getHasOfferTypeContent(guaranteedOfferType) ? (
                        <Box gridColumn="1/13">
                            <GuaranteedOfferQuestion
                                onTooltipPress={() =>
                                    updateModalState({
                                        activeModal:
                                            ModalType.GuaranteedOfferModal,
                                    })
                                }
                            />
                        </Box>
                    ) : null}
                </Box>

                <Box gridColumn="1/13" pb="space50" as="section">
                    <CardPageProductLists card={scoredCard} />
                </Box>

                <Box gridColumn="1/13" pb="space50" as="section">
                    <Box mb="space20">
                        <Heading as="h2" variant="60">
                            Rates and Fees
                        </Heading>
                    </Box>

                    <Box>
                        <RatesAndFees
                            card={scoredCard}
                            guaranteedAPR={
                                scoredCard.guaranteedRate?.guaranteedAPR
                            }
                        />
                    </Box>
                </Box>

                <RepresentativeExampleModal
                    showModal={
                        modalState.activeModal ==
                        ModalType.RepresentativeExample
                    }
                    onModalClose={resetModalState}
                />
                <PreApprovedModal
                    showModal={modalState.activeModal == ModalType.PreApproved}
                    onModalClose={resetModalState}
                />
                <CreditLimitModal
                    showModal={
                        modalState.activeModal === ModalType.CreditLimitModal
                    }
                    onModalClose={resetModalState}
                />
                <MinimumCreditLimitModal
                    showModal={
                        modalState.activeModal == ModalType.MinimumCreditLimit
                    }
                    onModalClose={resetModalState}
                />
                <GuaranteedOfferModal
                    showModal={
                        modalState.activeModal ===
                        ModalType.GuaranteedOfferModal
                    }
                    onModalClose={resetModalState}
                    offerType={getGuaranteedOfferType({
                        creditCard: scoredCard,
                    })}
                    showOfferDurationContent={
                        isTotallySureOffer
                            ? true
                            : getShowOfferDurationContent({
                                  category: appQueryState.categoryId!,
                                  card: modalState.activeCard,
                              })
                    }
                />
            </Grid>
        </>
    )
}

export default ScoredCard
